import React from 'react';
import { Helmet } from 'react-helmet-async';

const SchemaOrgArticle = ({ article }) => {
  if (!article) return null;
  
  // Extract publisher data
  const publisher = article.publishers && article.publishers[0] 
    ? article.publishers[0] 
    : { name: article.publisher_name || article.publisher || 'Unknown Publisher' };
  
  // Extract author data
  const author = article.authors && article.authors[0] 
    ? article.authors[0] 
    : { name: article.author_name || article.author || 'Unknown Author' };
  
  // Format date properly
  const formatDate = (dateString) => {
    if (!dateString) return new Date().toISOString();
    return new Date(dateString).toISOString();
  };
  
  // Determine truthfulness category - following Google's ClaimReview guidelines
  const getTruthfulnessCategory = (score) => {
    if (!score && score !== 0) return 'False';
    if (score <= 30) return 'False';
    if (score <= 60) return 'Misleading';
    if (score <= 80) return 'MostlyTrue';
    return 'True';
  };
  
  // Get detailed review score for individual claims
  const getClaimRatingValue = (claimText) => {
    // If claim text contains specific keywords, use different ratings
    // This is a simple example - you might want to use the claim's specific rating if available
    const lowerCaseText = claimText?.toLowerCase() || '';
    
    if (lowerCaseText.includes('false') || lowerCaseText.includes('misleading')) {
      return 1; // False
    } else if (lowerCaseText.includes('partially')) {
      return 2; // Partially false
    } else if (lowerCaseText.includes('mostly')) {
      return 4; // Mostly true
    }
    
    // Default - consider the claim as false since we're primarily tracking misleading claims
    return 1;
  };
  
  // Convert overall rating to 1-5 scale for Google compatibility
  const convertRatingTo5Scale = (rating) => {
    if (rating === null || rating === undefined) return 1;
    return Math.max(1, Math.min(5, Math.round(rating / 20)));
  };
  
  // Prepare individual ClaimReview items for each claim
  const claimReviews = [];
  if (article.claims && article.claims.length > 0) {
    article.claims.forEach(claim => {
      const ratingValue = getClaimRatingValue(claim.claim_text);
      const textualRating = getTruthfulnessCategory(ratingValue * 20); // Convert 1-5 scale to 0-100 for our function
      
      claimReviews.push({
        '@context': 'https://schema.org',
        '@type': 'ClaimReview',
        datePublished: formatDate(article.analysis_date || article.publication_date),
        url: window.location.href,
        itemReviewed: {
          '@type': 'Claim',
          author: {
            '@type': 'Person',
            name: author.name
          },
          datePublished: formatDate(article.publication_date),
          appearance: {
            '@type': 'MediaObject',
            url: article.url || window.location.href
          },
          name: claim.claim_text
        },
        claimReviewed: claim.claim_text,
        author: {
          '@type': 'Organization',
          name: 'Democracy Analysis Project',
          url: window.location.origin
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: ratingValue,
          bestRating: 5,
          worstRating: 1,
          alternateName: textualRating
        },
        textualRating: textualRating,
        reviewBody: claim.rebuttal_text || 'This claim is misleading or inaccurate.'
      });
    });
  }
  
  // Build main Article structured data with improved SEO
  const articleData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    datePublished: formatDate(article.publication_date),
    dateModified: formatDate(article.analysis_date || article.updated_at || article.publication_date),
    author: {
      '@type': 'Person',
      name: author.name,
      url: author.url
    },
    publisher: {
      '@type': 'Organization',
      name: publisher.name,
      url: publisher.url,
      logo: publisher.logo_url ? {
        '@type': 'ImageObject',
        url: publisher.logo_url
      } : {
        '@type': 'ImageObject',
        url: `${window.location.origin}/icons/favicon-512x512.png`,
        width: '512',
        height: '512'
      }
    },
    description: article.summary || `Analysis of "${article.title}"`,
    url: article.url || window.location.href,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': window.location.href
    },
    // Add image if available from publisher or default to site logo
    image: publisher.logo_url ? publisher.logo_url : `${window.location.origin}/images/social-share.jpg`,
    // Add article body for better SEO
    articleBody: article.content && article.content.text ? 
      article.content.text.substring(0, 5000) : // Limit to reasonable size
      article.summary || `Analysis of "${article.title}"`
  };
  
  // Build main fact check markup - using proper ClaimReview type
  const factCheckData = {
    '@context': 'https://schema.org',
    '@type': 'ClaimReview',
    datePublished: formatDate(article.analysis_date || article.publication_date),
    url: window.location.href,
    itemReviewed: {
      '@type': 'Claim',
      appearance: {
        '@type': 'MediaObject',
        url: article.url || window.location.href
      },
      datePublished: formatDate(article.publication_date),
      name: article.title,
      author: {
        '@type': 'Person',
        name: author.name
      }
    },
    claimReviewed: article.title,
    author: {
      '@type': 'Organization',
      name: 'Democracy Analysis Project',
      url: window.location.origin
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: convertRatingTo5Scale(article.overall_rating),
      bestRating: 5,
      worstRating: 1,
      alternateName: getTruthfulnessCategory(article.overall_rating)
    },
    textualRating: getTruthfulnessCategory(article.overall_rating),
    reviewBody: article.summary || 'No summary available.'
  };

  // Create an array of all schema.org entities to include
  const schemaDataArray = [articleData, factCheckData];
  
  // Add individual claim reviews if available
  if (claimReviews.length > 0) {
    schemaDataArray.push(...claimReviews);
  }

  return (
    <Helmet>
      {schemaDataArray.map((schemaItem, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schemaItem)}
        </script>
      ))}
    </Helmet>
  );
};

export default SchemaOrgArticle;